import type {CountryCodeType} from '@resurs-internal/rb-app-api/model/Country';
import type {FeatureToggle} from './unleash';

// Flag values in production Unleash on 2026-11-03, fetched per country
// without a userId. Used only while Unleash is unreachable or still loading;
// a flag missing here counts as on. Update by running: npm run unleash
export const productionFallbackFlags: Record<
  CountryCodeType,
  Readonly<Record<FeatureToggle, boolean>>
> = {
  SE: {
    'mp-agreement-cancellation': true,
    'mp-bill-payment-open-banking-long-poll': true,
    'mp-bill-payment-session-payments': false,
    'mp-bills': true,
    'mp-bills-conversion': false,
    'mp-bills-payment': true,
    'mp-bills-return': true,
    'mp-bills-snooze': true,
    'mp-bonus-check': true,
    'mp-bonus-point-shop': true,
    'mp-card-3ds-password': false,
    'mp-card-activation-extended-bank-id-enabled': false,
    'mp-card-activation-extended-bank-id-forced': false,
    'mp-card-pan-cvc': true,
    'mp-card-pin': true,
    'mp-click-to-pay': true,
    'mp-close-account-mambu': true,
    'mp-close-account-request': true,
    'mp-consent-v2': true,
    'mp-credit-account-payment': true,
    'mp-deposit-account-offer-dk': false,
    'mp-digital-invoice-notification': true,
    'mp-digital-invoice-notification-event': false,
    'mp-digital-invoice-notification-force-accept': false,
    'mp-discover-v2': true,
    'mp-documents': true,
    'mp-family-card': true,
    'mp-fullstory': true,
    'mp-login-signicat': true,
    'mp-new-version-button': true,
    'mp-offers-ab-test': false,
    'mp-offers-cards-ab-test': false,
    'mp-offers-deposit-ab-test': false,
    'mp-offers-loans-ab-test': false,
    'mp-payment-free-month': false,
    'mp-pm-use-new-detail-page': true,
    'mp-pm-use-new-transactions-page': true,
    'mp-ppe260-success-screen': true,
    'mp-ppi-ks': true,
    'mp-savings-deposit-funds': true,
    'mp-savings-deposit-funds-bank-transfer': true,
    'mp-savings-deposit-funds-direct-transfer': true,
    'mp-savings-deposit-funds-monthly': true,
    'mp-savings-internal-transfer': true,
    'mp-savings-onboarding': true,
    'mp-savings-statements': true,
    'mp-savings-withdrawal': true,
    'mp-savings-withdrawal-receiver': true,
    'mp-savings-withdrawal-receiver-link': false,
    'mp-statement-payment-connected-account': true,
    'mp-statement-payment-open-banking': false,
    'mp-statement-payment-open-banking-date': false,
    'mp-statement-payment-ppi': true,
    'mp-statement-session-payments': false,
    'mp-statements': true,
    'mp-theme-pink-ribbon': false,
    'mp-trustpilot-review': true,
    'mp-update-customer-information-notification': false,
    'mp-zendesk': true,
    'mp-zendesk-portal': true,
  },
  DK: {
    'mp-agreement-cancellation': true,
    'mp-bill-payment-open-banking-long-poll': true,
    'mp-bill-payment-session-payments': false,
    'mp-bills': true,
    'mp-bills-conversion': false,
    'mp-bills-payment': false,
    'mp-bills-return': false,
    'mp-bills-snooze': false,
    'mp-bonus-check': true,
    'mp-bonus-point-shop': true,
    'mp-card-3ds-password': false,
    'mp-card-activation-extended-bank-id-enabled': false,
    'mp-card-activation-extended-bank-id-forced': false,
    'mp-card-pan-cvc': true,
    'mp-card-pin': true,
    'mp-click-to-pay': true,
    'mp-close-account-mambu': true,
    'mp-close-account-request': true,
    'mp-consent-v2': true,
    'mp-credit-account-payment': true,
    'mp-deposit-account-offer-dk': false,
    'mp-digital-invoice-notification': false,
    'mp-digital-invoice-notification-event': false,
    'mp-digital-invoice-notification-force-accept': false,
    'mp-discover-v2': true,
    'mp-documents': true,
    'mp-family-card': true,
    'mp-fullstory': true,
    'mp-login-signicat': true,
    'mp-new-version-button': true,
    'mp-offers-ab-test': false,
    'mp-offers-cards-ab-test': false,
    'mp-offers-deposit-ab-test': false,
    'mp-offers-loans-ab-test': false,
    'mp-payment-free-month': true,
    'mp-pm-use-new-detail-page': true,
    'mp-pm-use-new-transactions-page': true,
    'mp-ppe260-success-screen': true,
    'mp-ppi-ks': true,
    'mp-savings-deposit-funds': true,
    'mp-savings-deposit-funds-bank-transfer': true,
    'mp-savings-deposit-funds-direct-transfer': true,
    'mp-savings-deposit-funds-monthly': false,
    'mp-savings-internal-transfer': true,
    'mp-savings-onboarding': true,
    'mp-savings-statements': true,
    'mp-savings-withdrawal': true,
    'mp-savings-withdrawal-receiver': true,
    'mp-savings-withdrawal-receiver-link': false,
    'mp-statement-payment-connected-account': false,
    'mp-statement-payment-open-banking': true,
    'mp-statement-payment-open-banking-date': false,
    'mp-statement-payment-ppi': true,
    'mp-statement-session-payments': false,
    'mp-statements': true,
    'mp-theme-pink-ribbon': false,
    'mp-trustpilot-review': true,
    'mp-update-customer-information-notification': false,
    'mp-zendesk': true,
    'mp-zendesk-portal': true,
  },
  NO: {
    'mp-agreement-cancellation': true,
    'mp-bill-payment-open-banking-long-poll': true,
    'mp-bill-payment-session-payments': false,
    'mp-bills': true,
    'mp-bills-conversion': false,
    'mp-bills-payment': true,
    'mp-bills-return': true,
    'mp-bills-snooze': true,
    'mp-bonus-check': true,
    'mp-bonus-point-shop': true,
    'mp-card-3ds-password': false,
    'mp-card-activation-extended-bank-id-enabled': false,
    'mp-card-activation-extended-bank-id-forced': false,
    'mp-card-pan-cvc': true,
    'mp-card-pin': true,
    'mp-click-to-pay': true,
    'mp-close-account-mambu': true,
    'mp-close-account-request': true,
    'mp-consent-v2': true,
    'mp-credit-account-payment': true,
    'mp-deposit-account-offer-dk': false,
    'mp-digital-invoice-notification': true,
    'mp-digital-invoice-notification-event': false,
    'mp-digital-invoice-notification-force-accept': false,
    'mp-discover-v2': true,
    'mp-documents': true,
    'mp-family-card': true,
    'mp-fullstory': true,
    'mp-login-signicat': true,
    'mp-new-version-button': true,
    'mp-offers-ab-test': false,
    'mp-offers-cards-ab-test': false,
    'mp-offers-deposit-ab-test': false,
    'mp-offers-loans-ab-test': false,
    'mp-payment-free-month': true,
    'mp-pm-use-new-detail-page': true,
    'mp-pm-use-new-transactions-page': true,
    'mp-ppe260-success-screen': true,
    'mp-ppi-ks': true,
    'mp-savings-deposit-funds': true,
    'mp-savings-deposit-funds-bank-transfer': true,
    'mp-savings-deposit-funds-direct-transfer': false,
    'mp-savings-deposit-funds-monthly': false,
    'mp-savings-internal-transfer': true,
    'mp-savings-onboarding': true,
    'mp-savings-statements': true,
    'mp-savings-withdrawal': true,
    'mp-savings-withdrawal-receiver': true,
    'mp-savings-withdrawal-receiver-link': false,
    'mp-statement-payment-connected-account': false,
    'mp-statement-payment-open-banking': true,
    'mp-statement-payment-open-banking-date': false,
    'mp-statement-payment-ppi': true,
    'mp-statement-session-payments': false,
    'mp-statements': true,
    'mp-theme-pink-ribbon': false,
    'mp-trustpilot-review': true,
    'mp-update-customer-information-notification': false,
    'mp-zendesk': true,
    'mp-zendesk-portal': true,
  },
  FI: {
    'mp-agreement-cancellation': true,
    'mp-bill-payment-open-banking-long-poll': true,
    'mp-bill-payment-session-payments': false,
    'mp-bills': true,
    'mp-bills-conversion': false,
    'mp-bills-payment': false,
    'mp-bills-return': false,
    'mp-bills-snooze': false,
    'mp-bonus-check': true,
    'mp-bonus-point-shop': true,
    'mp-card-3ds-password': true,
    'mp-card-activation-extended-bank-id-enabled': false,
    'mp-card-activation-extended-bank-id-forced': false,
    'mp-card-pan-cvc': true,
    'mp-card-pin': true,
    'mp-click-to-pay': true,
    'mp-close-account-mambu': true,
    'mp-close-account-request': true,
    'mp-consent-v2': true,
    'mp-credit-account-payment': true,
    'mp-deposit-account-offer-dk': false,
    'mp-digital-invoice-notification': true,
    'mp-digital-invoice-notification-event': false,
    'mp-digital-invoice-notification-force-accept': false,
    'mp-discover-v2': true,
    'mp-documents': true,
    'mp-family-card': true,
    'mp-fullstory': true,
    'mp-login-signicat': true,
    'mp-new-version-button': true,
    'mp-offers-ab-test': false,
    'mp-offers-cards-ab-test': false,
    'mp-offers-deposit-ab-test': false,
    'mp-offers-loans-ab-test': false,
    'mp-payment-free-month': true,
    'mp-pm-use-new-detail-page': true,
    'mp-pm-use-new-transactions-page': true,
    'mp-ppe260-success-screen': true,
    'mp-ppi-ks': true,
    'mp-savings-deposit-funds': false,
    'mp-savings-deposit-funds-bank-transfer': false,
    'mp-savings-deposit-funds-direct-transfer': false,
    'mp-savings-deposit-funds-monthly': false,
    'mp-savings-internal-transfer': true,
    'mp-savings-onboarding': false,
    'mp-savings-statements': true,
    'mp-savings-withdrawal': true,
    'mp-savings-withdrawal-receiver': true,
    'mp-savings-withdrawal-receiver-link': false,
    'mp-statement-payment-connected-account': false,
    'mp-statement-payment-open-banking': true,
    'mp-statement-payment-open-banking-date': false,
    'mp-statement-payment-ppi': true,
    'mp-statement-session-payments': false,
    'mp-statements': true,
    'mp-theme-pink-ribbon': false,
    'mp-trustpilot-review': true,
    'mp-update-customer-information-notification': false,
    'mp-zendesk': true,
    'mp-zendesk-portal': true,
  },
};
